import { useFileComplaint } from "@complaints/complaintsQueries";
import { Button, Dialog, Textarea } from "@scribenote/brian-design-system";
import { useEffect, useRef, useState } from "react";

/**
 * TODO(BE-1951): TEMPORARY, first-clinic launch only. Delete src/complaints/, the
 * CompanionControls hotkey branch, and the @complaints alias when it ends.
 * On failure the typed message is kept.
 */
export const ComplaintModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const complaint = useFileComplaint();

  // A native <dialog> never unmounts, so reset the draft and send state on each open.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setMessage("");
  }
  const resetSend = complaint.reset;
  useEffect(() => {
    if (isOpen) resetSend();
  }, [isOpen, resetSend]);

  // showModal() picks its own focus target, so reclaim it one frame later.
  useEffect(() => {
    if (!isOpen) return;
    const id = requestAnimationFrame(() => textareaRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  const canSend = message.trim().length > 0 && !complaint.isPending;
  const send = () => {
    if (canSend) complaint.mutate(message.trim());
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      // Dropped while closed: a native <dialog> stays mounted.
      data-testid={isOpen ? "complaint-modal" : undefined}
      title="Report a problem"
      footer={
        complaint.isSuccess ? (
          <Button onClick={onClose} data-testid="complaint-done">
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="ghost"
              onClick={onClose}
              data-testid="complaint-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={send}
              disabled={!canSend}
              busy={complaint.isPending}
              data-testid="complaint-send"
            >
              {complaint.isPending ? "Sending" : "Send to Scribenote"}
            </Button>
          </>
        )
      }
    >
      {complaint.isSuccess ? (
        <p className="text-sm text-ink-body" data-testid="complaint-sent">
          Sent. The Scribenote team has been notified.
        </p>
      ) : (
        <div className="mx-auto flex max-w-[38rem] flex-col gap-3">
          <p className="text-sm text-annotation">
            Goes straight to the Scribenote team with recent activity attached.
          </p>
          <Textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="What went wrong? What were you trying to do?"
            minRows={4}
            maxRows={12}
            disabled={complaint.isPending}
            error={
              complaint.isError
                ? "Couldn't send, and your text is kept. Try again, or text the Scribenote team directly."
                : undefined
            }
            data-testid="complaint-textarea"
          />
        </div>
      )}
    </Dialog>
  );
};
